import moment from 'moment';
import * as yup from 'yup';
import { constants } from '@clubmed/utils';
import { t } from 'Src/i18n';
const { EVENT_TYPES, CLIENT_TYPES, USER_SALUTATIONS } = constants;
export const FORM_ACTIONS = {
  SUBMIT_EVENT: 'SUBMIT_EVENT',
  SUBMIT_EVENT_ERROR: 'SUBMIT_EVENT_ERROR',
  SUBMIT_EVENT_SUCCESS: 'SUBMIT_EVENT_SUCCESS',
};
export const FORM_STATUS = {
  NEW: 'neutral',
  SUCCESS: 'success',
  ERROR: 'error',
};
export const getInitialFormValues = () => ({
  type: '',
  numberOfParticipants: '',
  withRooms: false,
  numberOfRooms: '',
  withServices: false,
  withTransport: false,
  departureCity: '',
  logisticsComment: '',
  withMeetingRooms: false,
  region: '',
  resort: '',
  dates: '',
  withFlexibleDates: false,
  company: '',
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  postcode: '',
  city: '',
  country: '',
  generalComment: '',
  optinEmail: false,
  optinSMS: false,
  optinPhone: false,
});
export type EventFormFieldValues = ReturnType<typeof getInitialFormValues>;
type EventFormValues = {
  address: string;
  city: string;
  company: string;
  country: string;
  dates: {
    from: Date;
    to: Date;
  };
  departureCity: string;
  email: string;
  firstName: string;
  generalComment: string;
  lastName: string;
  logisticsComment: string;
  numberOfParticipants: number;
  numberOfRooms: number;
  optinEmail: boolean;
  optinPersonalInformation: boolean;
  optinPhone: boolean;
  optinSMS: boolean;
  phone: string;
  postcode: string;
  region: string;
  resort: string;
  salutation: string;
  type: string;
  withFlexibleDates: boolean;
  withMeetingRooms: boolean;
  withRooms: boolean;
  withTransport: boolean;
};

const encodeStringValues = (values): EventFormValues => {
  const encodedValues = {};
  Object.keys(values).forEach((key) => {
    if (typeof values[key] === 'string') {
      encodedValues[key] = encodeURIComponent(values[key]);
    }
  });
  return { ...values, ...encodedValues };
};

export const getEventQuery = (values: EventFormValues, formType: 'classic' | 'affinity') => {
  const {
    address,
    city,
    company,
    country,
    dates,
    departureCity,
    email,
    firstName,
    generalComment,
    lastName,
    logisticsComment,
    numberOfParticipants,
    numberOfRooms,
    optinEmail,
    optinPhone,
    optinSMS,
    phone,
    postcode,
    region,
    resort,
    salutation,
    type,
    withFlexibleDates,
    withMeetingRooms,
    withRooms,
    withTransport,
  } = encodeStringValues(values);
  // We need to encode special chars in order to send them via the graphQL query
  const departureDate = dates.from ? `"${moment(dates.from).format('YYYY-MM-DD')}"` : 'null';
  const returnDate = dates.to ? `"${moment(dates.to).format('YYYY-MM-DD')}"` : 'null';
  const numberOfRoomsValue = withRooms ? numberOfRooms : 'null';
  const salutationValue = salutation ? salutation : 'null';
  return `mutation {
    event(eventInput: {
      address: "${address}",
      city: "${city}",
      company: "${company}",
      country: "${country}",
      departureDate: ${departureDate},
      departureCity: "${departureCity}",
      email: "${email}",
      firstName: "${firstName}",
      generalComment: "${generalComment}",
      interest: ${type},
      lastName: "${lastName}",
			logisticsComment: "${logisticsComment}",
      numberOfParticipants: ${numberOfParticipants},
      numberOfRooms: ${numberOfRoomsValue},
      optinEmail: ${optinEmail.toString()},
      optinPhone: ${optinPhone.toString()},
      optinSMS: ${optinSMS.toString()},
      phone: "${phone}",
      postcode: "${postcode}",
      region: "${region}",
      resort: "${resort}",
      returnDate: ${returnDate},
      salutation: ${salutationValue},
      withFlexibleDates: ${withFlexibleDates.toString()},
      withMeetingRooms: ${withMeetingRooms.toString()},
      withTransport: ${withTransport.toString()},
    }, formType: "${formType}") {
      __typename
      ...on EventError {
        statusCode
        statusMessage
        errorCode
        errorDescription
      }
      ...on Event {
        id
      }
    }
  }`;
};
type getEventSchemaProps = {
  displayAddressFields: boolean;
  resortIds: Array<string>;
};
export const getEventSchema = ({ displayAddressFields, resortIds }: getEventSchemaProps) => {
  // N.B: the salesforce API as a max length limit of 255 characters for strings
  const addressGroupSchema = displayAddressFields
    ? {
        address: yup.string().required(t('eventForm.userSection.streetPlaceholder')).max(255),
        postcode: yup.string().required(t('eventForm.userSection.postcodePlaceholder')).max(255),
        city: yup.string().required(t('eventForm.userSection.cityPlaceholder')).max(255),
        country: yup.string().required(t('eventForm.userSection.countryPlaceholder')).max(255),
      }
    : null;
  return yup.object().shape({
    type: yup
      .mixed()
      .oneOf(Object.keys(EVENT_TYPES))
      .required(t('eventForm.packageSection.eventTypePlaceholder')),
    numberOfRooms: yup.number().min(1),
    withFlexibleDates: yup.boolean(),
    withMeetingRooms: yup.boolean(),
    withRooms: yup.boolean(),
    withServices: yup.boolean(),
    withTransport: yup.boolean(),
    departureCity: yup.string().max(255),
    logisticsComment: yup.string().max(255),
    region: yup.string(),
    resort: yup.mixed().oneOf(resortIds),
    dates: yup.object().shape({
      from: yup.date().nullable(),
      to: yup.date().nullable(),
    }),
    company: yup.string().max(255),
    salutation: yup
      .mixed()
      .oneOf(Object.keys(USER_SALUTATIONS))
      .required(t('eventForm.userSection.salutationPlaceholder')),
    firstName: yup.string().required(t('eventForm.userSection.firstNamePlaceholder')).max(255),
    lastName: yup.string().required(t('eventForm.userSection.lastNamePlaceholder')).max(255),
    email: yup
      .string()
      .email(t('eventForm.userSection.emailPlaceholder'))
      .required(t('eventForm.userSection.emailPlaceholder'))
      .max(255),
    numberOfParticipants: yup
      .number()
      .min(1, t('eventForm.packageSection.numberOfParticipantsPlaceholder'))
      .required(t('eventForm.packageSection.numberOfParticipantsPlaceholder')),
    phone: yup.string().required(t('eventForm.userSection.phonePlaceholder')).max(255),
    ...addressGroupSchema,
    generalComment: yup.string().max(255),
    optinEmail: yup.boolean(),
    optinSMS: yup.boolean(),
    optinPhone: yup.boolean(),
  });
};
export const getEventAffinitySchema = ({
  displayAddressFields,
  resortIds,
}: getEventSchemaProps) => {
  // N.B: the salesforce API as a max length limit of 255 characters for strings
  const addressGroupSchema = displayAddressFields
    ? {
        address: yup.string().max(255),
        postcode: yup.string().max(255),
        city: yup.string().max(255),
        country: yup.string().max(255),
      }
    : null;
  return yup.object().shape({
    type: yup
      .mixed()
      .oneOf(Object.keys(CLIENT_TYPES))
      .required(t('eventForm.userSection.clientTypePlaceholder')),
    region: yup.string(),
    resort: yup.mixed().oneOf(resortIds),
    dates: yup
      .object()
      .shape({
        from: yup.date().nullable(),
        to: yup.date().nullable(),
      })
      .required(t('eventForm.groupSection.datesPlaceholder')),
    company: yup.string().max(255),
    firstName: yup.string().required(t('eventForm.userSection.firstNamePlaceholder')).max(255),
    lastName: yup.string().required(t('eventForm.userSection.lastNamePlaceholder')).max(255),
    email: yup
      .string()
      .email(t('eventForm.userSection.emailPlaceholder'))
      .required(t('eventForm.userSection.emailPlaceholder'))
      .max(255),
    numberOfParticipants: yup
      .number()
      .min(1, t('eventForm.groupSection.numberOfParticipantsPlaceholder'))
      .required(t('eventForm.groupSection.numberOfParticipantsPlaceholder')),
    phone: yup.string().max(255),
    ...addressGroupSchema,
    generalComment: yup.string().max(255),
    optinEmail: yup.boolean(),
    optinSMS: yup.boolean(),
    optinPhone: yup.boolean(),
  });
};
